import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Award } from 'lucide-react'
import { api, errorMessage } from '@/lib/api'
import { todayInMalaysia } from '@/lib/date'
import { money } from '@/lib/money'
import type { CustomerRow, Tier } from '@/types'
import Button from '@/components/ui/Button'
import { Field, Input } from '@/components/ui/Field'
import Modal from '@/components/ui/Modal'
import { Select } from '@/components/ui/Select'
import Toggle from '@/components/ui/Toggle'
import { useToast } from '@/components/ui/toast'

/**
 * Set a customer's tier by hand (client request 2026-10-06: existing agents
 * should not start at the bottom tier), the way Printscape does it. Locked,
 * the tier holds through top-ups and tier edits until the expiry day, or for
 * good when none is given. Unlocked, it holds only until the next top-up or
 * tier edit works it out again from lifetime top-up.
 */
export default function SetTierModal({ customer, onClose }: { customer: CustomerRow | null; onClose: () => void }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [tierId, setTierId] = useState('')
  const [locked, setLocked] = useState(true)
  const [expiresOn, setExpiresOn] = useState('')

  // Same key and shape as admin > Tiers, so the two share one cache entry.
  const tiers = useQuery({
    queryKey: ['admin-pricing'],
    queryFn: async () => (await api.get<{ tiers: Tier[] }>('/admin/pricing')).data,
    enabled: Boolean(customer),
  })

  useEffect(() => {
    if (!customer) return
    setTierId(customer.tier ? String(customer.tier.id) : '')
    // A customer whose tier was only ever earned opens with the lock on: that
    // is what setting a tier by hand is for in almost every case.
    setLocked(customer.tier_set_by_name ? Boolean(customer.tier_locked) : true)
    setExpiresOn(customer.tier_lock_expires_at ?? '')
    // Reset only when the dialog opens on a customer, not on every refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer?.id])

  const today = todayInMalaysia()
  const expiryError = locked && expiresOn !== '' && expiresOn < today ? 'Pick today or a later day.' : undefined
  const ready = tierId !== '' && !expiryError

  const save = useMutation({
    mutationFn: async (id: number) =>
      (
        await api.patch<{ message?: string }>(`/admin/customers/${id}/tier`, {
          tier_id: Number(tierId),
          tier_locked: locked,
          tier_lock_expires_at: locked && expiresOn ? expiresOn : null,
        })
      ).data,
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['customers-page'] })
      onClose()
      toast.success(res.message ?? 'Tier updated')
    },
    onError: (err) => toast.error('Could not set the tier', errorMessage(err)),
  })

  const options = (tiers.data?.tiers ?? []).map((tier) => ({
    value: String(tier.id),
    label: `${tier.name} (from ${money(tier.topup_threshold ?? 0)})`,
  }))

  return (
    <Modal
      open={Boolean(customer)}
      onClose={onClose}
      title={customer ? `Set tier - ${customer.name}` : ''}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!ready} loading={save.isPending} onClick={() => customer && save.mutate(customer.id)}>
            <Award className="size-4" /> Save tier
          </Button>
        </>
      }
    >
      {customer && (
        <div className="space-y-4">
          <Field label="Tier" htmlFor="customer-tier">
            <Select
              id="customer-tier"
              value={tierId}
              onChange={setTierId}
              options={options}
              placeholder={tiers.isLoading ? 'Loading tiers...' : 'Choose a tier'}
            />
          </Field>
          <Toggle
            checked={locked}
            onChange={setLocked}
            label="Lock tier"
            hint="Top-ups and tier changes will not move this customer while locked."
          />
          {locked ? (
            <Field label="Lock expires on (optional)" htmlFor="customer-tier-expiry" error={expiryError}>
              <Input
                id="customer-tier-expiry"
                type="date"
                min={today}
                value={expiresOn}
                onChange={(e) => setExpiresOn(e.target.value)}
              />
              <p className="mt-1 text-xs text-muted">
                Leave blank to keep it locked until you change it. After this day the tier stays until their next
                top-up works it out again.
              </p>
            </Field>
          ) : (
            <p className="rounded-lg border border-line bg-surface p-2.5 text-xs text-muted">
              Without the lock this tier lasts only until their next top-up, or until a tier is edited, when it is
              worked out again from lifetime top-up.
            </p>
          )}
        </div>
      )}
    </Modal>
  )
}
